// websocket.service.ts - VERSIÓN CORREGIDA
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { Helps } from 'src/app/libs/helps';
import { Users } from '../models/interfaces/users';

export interface WebSocketMessage {
  type: string;
  timestamp: string;
  data: any;
  priority?: string;
}

export interface ChatMessage {
  id: string;
  type: 'incoming' | 'outgoing' | 'system';
  content?: string;           // Campo principal para el contenido
  message?: string;           // Campo alternativo del servidor
  timestamp: Date;
  sender?: string;
  guard_id?: string;
  facility_id?: string;
  from_guard_id?: string;     // Mensajes desde guardias
  from_operator_id?: string;  // Mensajes desde operadores/central
  guard_name?: string;        // Nombre del guardia
  operator_name?: string;     // Nombre del operador
}

export interface ActiveGuardSocket {
  guard_id: string;
  guard_name: string;
  facility_id: string;
  facility_name: string;
  status: 'online' | 'offline' | 'busy';
  last_activity: string;
  location?: {
    lat: number | null;
    lng: number | null;
  };
}

// Interfaz actualizada según PANIC_BUTTON_FRONTEND_GUIDE
export interface PanicAlertData {
  id: string;                         // Puede ser id o alert_id
  alert_id?: string;                   // UUID único
  guard_id: string;
  guard_name: string;
  device_id?: string;
  facility_id: string;
  facility_name: string;
  alert_type: 'panic_button' | 'manual' | 'automatic';
  location_lat?: number;               // Decimal(10,8)
  location_lng?: number;               // Decimal(11,8)
  location_accuracy?: number;          // Decimal(6,2)
  description: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  status?: 'active' | 'acknowledged' | 'resolved' | 'false_alarm';
  location_data?: any;                 // JSON opcional
  additional_data?: any;               // JSON opcional
  created_at: string;                  // ISO timestamp
  acknowledged_at?: string;            // ISO timestamp
  resolved_at?: string;                // ISO timestamp
  resolved_by?: string;
  isOldAlert?: boolean;                // Flag para alertas antiguas (>5h)
}

@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000;
  
  private connectionStatus = new BehaviorSubject<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  public connectionStatus$ = this.connectionStatus.asObservable();
  
  private messageSubject = new Subject<WebSocketMessage>();
  public messages$ = this.messageSubject.asObservable();
  
  private panicAlertSubject = new BehaviorSubject<PanicAlertData | null>(null);
  public panicAlerts$ = this.panicAlertSubject.asObservable();

  private activeGuardsSubject = new BehaviorSubject<ActiveGuardSocket[]>([]);
  public activeGuards$ = this.activeGuardsSubject.asObservable();

  private chatMessagesSubject = new Subject<ChatMessage>();
  public chatMessages$ = this.chatMessagesSubject.asObservable();

  private currentHistoryRequestGuardId: string | null = null;
  private currentHistoryRequestFacilityId: string | null = null;
  private facilitiesToMonitor: string[] = [];
  private heartbeatInterval: any = null;

  constructor(private helps: Helps) {}

  connect(): void {
    // Bloquea reconexiones duplicadas mientras ya hay un socket conectado o conectándose
    if (this.socket && (this.socket.connected || this.connectionStatus.value === 'connecting')) {
      console.log('WebSocket ya conectado o conectando, se omite el intento de conexión');
      return;
    }

    console.log('🔌 Iniciando conexión WebSocket...');
    console.log('🔌 URL de conexión:', environment.websocketUrl);

    let tokenData;
    try {
      tokenData = this.helps.getToken();
      console.log('📋 Token data obtenido:', {
        hasToken: !!tokenData?.token,
        tokenLength: tokenData?.token?.length || 0,
        tokenStart: tokenData?.token?.substring(0, 30) + '...' || 'N/A',
        userId: tokenData?.id,
        userData: tokenData?.data
      });
    } catch (error) {
      console.error('❌ Error obteniendo token:', error);
      this.connectionStatus.next('error');
      return;
    }
    
    if (!tokenData || !tokenData.token) {
      console.error('❌ Token inválido o no disponible:', tokenData);
      this.connectionStatus.next('error');
      return;
    }

    this.connectionStatus.next('connecting');

    try {
      console.log('🌐 Conectando a:', environment.websocketUrl);
      
      // CORRECCIÓN PRINCIPAL: Estructura mejorada del auth object
      const userData = tokenData.data || {};
      const authConfig = {
        token: tokenData.token,
        // Campos adicionales que el servidor puede usar como fallback
        userId: tokenData.id || tokenData.sub || 'unknown_user',
        role: userData.role || tokenData.role || 'admin', // Rol específico para central
        userName: userData.name || userData.displayName || tokenData.displayName || 'Admin User'
      };

      console.log('🔐 Auth config preparado:', {
        hasToken: !!authConfig.token,
        userId: authConfig.userId,
        role: authConfig.role,
        userName: authConfig.userName
      });

      this.socket = io(environment.websocketUrl, {
        auth: authConfig,
        transports: ['websocket', 'polling'], // Permitir fallback a polling
        timeout: 20000, // Aumentado a 20s
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: 2000,
        reconnectionDelayMax: 10000,
        autoConnect: true,
        // Opciones adicionales para debugging
        upgrade: true,
        rememberUpgrade: true,
        // Agregar query params para debugging
        query: {
          clientType: 'angular_admin',
          version: environment.version || '1.0.0',
          timestamp: Date.now().toString()
        }
      });

      this.setupSocketListeners();

    } catch (error) {
      console.error('💥 Error creando socket:', error);
      this.connectionStatus.next('error');
      this.scheduleReconnect();
    }
  }

  private setupSocketListeners(): void {
    if (!this.socket) {
      console.error('❌ No se puede configurar listeners: socket es null');
      return;
    }

    console.log('🎧 Configurando listeners de socket...');

    this.socket.on('connect', () => {
      console.log('✅ Socket.IO conectado exitosamente');
      console.log('   - Socket ID:', this.socket?.id);
      console.log('   - Transport:', this.socket?.io?.engine?.transport?.name);
      console.log('   - URL:', environment.websocketUrl);
      console.log('   - Query:', this.socket?.io?.opts?.query);
      
      this.connectionStatus.next('connected');
      this.reconnectAttempts = 0;
      
      // Enviar ping inmediato para verificar conexión
      this.sendPing();
      
      // Registrar como central después de confirmar conexión
      setTimeout(() => {
        this.registerAsCentral();
      }, 2000); // Aumentado el delay
    });

    this.socket.on('disconnect', (reason: string) => {
      console.log('🔌 Socket.IO desconectado:', reason);
      this.connectionStatus.next('disconnected');
      
      // Mapear razones de desconexión para mejor debugging
      const reasonMap: { [key: string]: string } = {
        'io server disconnect': 'Servidor desconectó el cliente',
        'io client disconnect': 'Cliente desconectó',
        'ping timeout': 'Timeout de ping (conexión perdida)',
        'transport close': 'Transporte cerrado',
        'transport error': 'Error de transporte'
      };
      
      console.log('   - Razón detallada:', reasonMap[reason] || reason);
      
      if (reason === 'io server disconnect') {
        // Servidor desconectó, reconectar manualmente
        this.scheduleReconnect();
      }
      // Para otras razones, socket.io reconecta automáticamente
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('❌ Error de conexión Socket.IO:', error);
      console.error('   - Mensaje:', error.message);
      console.error('   - Tipo:', error.type);
      console.error('   - Descripción:', error.description);
      console.error('   - Stack:', error.stack);
      
      this.connectionStatus.next('error');
      
      // Análisis específico del error
      if (error.message?.includes('Token de autenticación inválido')) {
        console.error('🚨 ERROR DE AUTENTICACIÓN DETECTADO');
        console.error('   - Verificar que el token sea válido');
        console.error('   - Verificar que JWT_SECRET sea correcto en el servidor');
      }
      
      // No se programa una reconexión manual aquí: el cliente Socket.IO ya
      // reintenta internamente (reconnection: true) usando el mismo socket.
      // Volver a llamar a connect()/scheduleReconnect() en este punto creaba
      // un segundo cliente io() huérfano cada vez que fallaba la conexión.
    });

    // CORRECCIÓN: Listener para confirmación de registro
    this.socket.on('central_registered', (data: any) => {
      console.log('✅ Registro como central confirmado:', data);
      this.messageSubject.next({
        type: 'central_registered',
        timestamp: new Date().toISOString(),
        data: data
      });
    });

    // Listen for panic alerts - Según PANIC_BUTTON_FRONTEND_GUIDE
    this.socket.on('new_panic_alert', (data: any) => {
      console.log('🚨 Alerta de pánico recibida:', data);
      console.log('🔍 Estructura de datos completa:', JSON.stringify(data, null, 2));

      // Según la guía: data.alert contiene el objeto PanicAlert completo
      if (data.alert) {
        const alertData: PanicAlertData = {
          id: data.alert.id?.toString() || data.alert.alert_id || 'temp_' + Date.now(),
          alert_id: data.alert.alert_id,
          guard_id: data.alert.guard_id?.toString(),
          guard_name: data.alert.guard_name,
          device_id: data.alert.device_id,
          facility_id: data.alert.facility_id?.toString(),
          facility_name: data.alert.facility_name || 'Instalación desconocida',
          alert_type: (data.alert.alert_type as 'panic_button' | 'manual' | 'automatic') || 'panic_button',
          location_lat: data.alert.location_lat,
          location_lng: data.alert.location_lng,
          location_accuracy: data.alert.location_accuracy,
          description: data.alert.description || 'Alerta de pánico activada',
          priority: (data.alert.priority as 'low' | 'medium' | 'high' | 'critical') || 'critical',
          status: (data.alert.status as 'active' | 'acknowledged' | 'resolved' | 'false_alarm') || 'active',
          location_data: data.alert.location_data,
          additional_data: data.alert.additional_data,
          created_at: data.alert.created_at || new Date().toISOString(),
          acknowledged_at: data.alert.acknowledged_at,
          resolved_at: data.alert.resolved_at,
          resolved_by: data.alert.resolved_by
        };
        
        // Verificar si la alerta es muy antigua (más de 5 horas)
        const alertTime = new Date(alertData.created_at).getTime();
        const currentTime = Date.now();
        const fiveHoursInMs = 5 * 60 * 60 * 1000;
        const timeDifference = currentTime - alertTime;
        
        if (timeDifference > fiveHoursInMs) {
          console.log('⚠️ WEBSOCKET: Alerta muy antigua (>5h), marcando como antigua');
          alertData.isOldAlert = true;
        }
        
        console.log('📤 Emitiendo alerta procesada:', alertData);
        this.panicAlertSubject.next(alertData);
        
        // También emitir como mensaje general
        this.messageSubject.next({
          type: 'new_panic_alert',
          timestamp: alertData.created_at,
          data: alertData,
          priority: alertData.priority
        });
      } else {
        console.error('❌ Formato de alerta incorrecto - falta campo alert:', data);
      }
    });

    // Listen for alert acknowledgments - Según PANIC_BUTTON_FRONTEND_GUIDE
    this.socket.on('alert_acknowledged', (data: any) => {
      console.log('✅ Alerta confirmada:', data);
      // data: { alert_id: string, acknowledged_by: number, timestamp: string }
      this.messageSubject.next({
        type: 'alert_acknowledged',
        timestamp: data.timestamp,
        data: data
      });
    });

    // Listen for register success - Según PANIC_BUTTON_FRONTEND_GUIDE
    this.socket.on('register_success', (data: any) => {
      console.log('✅ Registro exitoso:', data);
      this.messageSubject.next({
        type: 'register_success',
        timestamp: new Date().toISOString(),
        data: data
      });
    });

    // Listen for active guards updates
    this.socket.on('active_guards_update', (data: any) => {
      console.log('📋 Actualización de guardias activos:', data);

      if (data.guards && Array.isArray(data.guards)) {
        const activeGuards: ActiveGuardSocket[] = data.guards.map((guard: any) => ({
          guard_id: guard.guard_id || guard.id,
          guard_name: guard.guard_name || guard.name,
          facility_id: guard.facility_id?.toString(),
          facility_name: guard.facility_name || guard.facility,
          status: guard.status || 'online',
          last_activity: guard.last_activity || guard.timestamp || new Date().toISOString(),
          location: guard.location || null
        }));

        this.activeGuardsSubject.next(activeGuards);
        console.log('📤 Guardias activos actualizados:', activeGuards);
      }
    });

    // Listen for individual guard status changes
    this.socket.on('guard_status_change', (data: any) => {
      console.log('👤 Cambio de estado de guardia:', data);

      const currentGuards = this.activeGuardsSubject.value;
      const updatedGuards = currentGuards.map(guard => {
        if (guard.guard_id === data.guard_id) {
          return {
            ...guard,
            status: data.status,
            last_activity: data.timestamp || new Date().toISOString()
          };
        }
        return guard;
      });

      this.activeGuardsSubject.next(updatedGuards);
    });

    // Listen for message confirmations
    this.socket.on('message_sent_success', (data: any) => {
      console.log('✅ Mensaje enviado exitosamente:', data);
      this.messageSubject.next({
        type: 'message_sent_success',
        timestamp: new Date().toISOString(),
        data: data
      });
    });

    // Listen for incoming chat messages
    this.socket.on('new_chat_message', (data: any) => {
      console.log('💬 Nuevo mensaje de chat recibido:', data);
      console.log('🔍 Estructura del mensaje:', JSON.stringify(data, null, 2));

      // Limpiar contenido del mensaje
      let cleanContent = data.message || data.content || '';
      
      // Remover prefijo [FACILITY:X] y reemplazar con [Guardia]
      if (cleanContent.match(/^\[FACILITY:\d+\]/)) {
        cleanContent = cleanContent.replace(/^\[FACILITY:\d+\]\s*/, '[Guardia] ');
      }

      const chatMessage: ChatMessage = {
        id: data.id?.toString() || Date.now().toString(),
        type: this.determineMessageType(data),
        content: cleanContent,
        message: cleanContent,
        timestamp: new Date(data.timestamp || new Date()),
        sender: data.guard_name || data.sender_name || data.operator_name || 
                (data.type === 'from_guard' ? 'Guardia' : 
                 data.type === 'from_operator' ? 'Central' : 'Sistema'),
        guard_id: data.from_guard_id || data.guard_id,
        facility_id: data.facility_id,
        from_guard_id: data.from_guard_id,
        from_operator_id: data.from_operator_id,
        guard_name: data.guard_name,
        operator_name: data.operator_name
      };

      console.log('📤 Mensaje procesado:', chatMessage);
      this.chatMessagesSubject.next(chatMessage);
    });

    // Listen for chat history response
    this.socket.on('chat_history_response', (data: any) => {
      console.log('📜 Historial de chat recibido:', data);

      // Check for both 'history' and 'messages' array formats
      const historyMessages = data.history || data.messages;
      if (historyMessages && Array.isArray(historyMessages)) {
        console.log(`📋 Procesando ${historyMessages.length} mensajes del historial`);

        // Process history messages and emit them
        historyMessages.forEach((msg: any) => {
          console.log('📜 Procesando mensaje del historial:', msg);
          
          // Limpiar contenido del mensaje del historial
          let cleanContent = msg.message || msg.content || '';
          
          // Remover prefijo [FACILITY:X] y reemplazar con [Guardia]
          if (cleanContent.match(/^\[FACILITY:\d+\]/)) {
            cleanContent = cleanContent.replace(/^\[FACILITY:\d+\]\s*/, '[Guardia] ');
          }
          
          const chatMessage: ChatMessage = {
            id: msg.id?.toString() || (Date.now().toString() + Math.random()),
            type: this.determineMessageType(msg),
            content: cleanContent,
            message: cleanContent,
            timestamp: new Date(msg.timestamp || new Date()),
            sender: msg.guard_name || msg.sender_name || msg.operator_name || 
                    (msg.type === 'from_guard' ? 'Guardia' : 
                     msg.type === 'from_operator' ? 'Central' : 'Sistema'),
            guard_id: this.currentHistoryRequestGuardId || msg.from_guard_id || msg.guard_id,
            facility_id: this.currentHistoryRequestFacilityId || msg.facility_id,
            from_guard_id: msg.from_guard_id,
            from_operator_id: msg.from_operator_id,
            guard_name: msg.guard_name,
            operator_name: msg.operator_name
          };

          console.log('📤 Emitiendo mensaje del historial procesado:', chatMessage);
          this.chatMessagesSubject.next(chatMessage);
        });
      } else {
        console.log('⚠️ No se encontraron mensajes en el historial o formato incorrecto');
      }
    });

    // Listen for incident created from mobile apps
    this.socket.on('incident_created', (data: any) => {
      console.log('📨 Nueva incidencia creada desde app móvil:', data);
      this.messageSubject.next({
        type: 'incident_created',
        timestamp: data.timestamp || new Date().toISOString(),
        data: data
      });
    });

    // Listen for errors
    this.socket.on('error', (error: any) => {
      console.error('Socket.IO server error:', error);
      this.messageSubject.next({
        type: 'error',
        timestamp: new Date().toISOString(),
        data: error
      });
    });
    
    // Agregar listeners para eventos adicionales de debugging
    this.socket.on('pong', (data: any) => {
      console.log('🏓 Pong recibido del servidor:', data);
    });
    
    // Listener genérico para capturar CUALQUIER evento
    this.socket.onAny((eventName: string, ...args: any[]) => {
      console.log(`📡 EVENTO RECIBIDO: ${eventName}`, args);
      
      // Si es un evento no manejado, mostrarlo
      const handledEvents = [
        'connect', 'disconnect', 'connect_error', 'error', 'pong', 'ping',
        'new_panic_alert', 'alert_acknowledged', 'register_success', 
        'active_guards_update', 'guard_status_change', 'message_sent_success',
        'new_chat_message', 'chat_history_response', 'central_registered',
        'incident_created' // Nuevo evento agregado
      ];
      
      if (!handledEvents.includes(eventName)) {
        console.log(`⚠️ EVENTO NO MANEJADO: ${eventName}`, args);
      }
    });
    
    console.log('✅ Todos los listeners configurados correctamente');
  }

  disconnect(): void {
    console.log('🔌 Desconectando WebSocket...');
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.socket) {
      this.socket.offAny();
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.reconnectAttempts = 0;
    this.connectionStatus.next('disconnected');
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('🚫 Máximo número de intentos de reconexión alcanzado');
      this.connectionStatus.next('error');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), 30000);

    console.log(`⏰ Programando reconexión en ${delay}ms (intento ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (this.connectionStatus.value === 'connected') {
        return;
      }
      if (this.socket) {
        // Reutiliza el socket existente en vez de crear uno nuevo con connect(),
        // que instanciaba otro cliente io() y dejaba el anterior huérfano.
        console.log('🔄 Intentando reconexión con el socket existente...');
        this.socket.connect();
      } else {
        this.connect();
      }
    }, delay);
  }

  // CORRECCIÓN: Método renombrado y mejorado
  private registerAsCentral(): void {
    if (!this.socket) {
      console.error('❌ No se puede registrar central - socket no disponible');
      return;
    }

    if (!this.socket.connected) {
      console.error('❌ No se puede registrar central - socket no conectado');
      return;
    }

    let tokenData;
    try {
      tokenData = this.helps.getToken();
    } catch (error) {
      console.error('❌ No se puede registrar central, token no disponible:', error);
      return;
    }

    const userData = tokenData?.data || {};
    
    // CORRECCIÓN: Usar los campos correctos que espera el servidor
    const registrationData = {
      operator_id: tokenData?.id || tokenData?.sub || 'admin_user_' + Date.now(),
      operator_name: userData.name || userData.displayName || tokenData?.displayName || 'Admin User',
      facilities: this.facilitiesToMonitor.length > 0 
        ? this.facilitiesToMonitor.map(Number) 
        : [1], // Default facility ID
      client_type: 'web_dashboard',
      timestamp: new Date().toISOString()
    };

    console.log('📡 Registrando como sistema central con datos:', registrationData);
    
    // Según PANIC_BUTTON_FRONTEND_GUIDE: usar register_central
    this.socket.emit('register_central', registrationData);
    
    console.log('📡 Facilities enviadas:', registrationData.facilities);

    // Timeout para verificar registro
    setTimeout(() => {
      if (this.socket && this.socket.connected) {
        console.log('✅ Proceso de registro de central completado');

        // Solicitar guardias activos después del registro
        this.requestActiveGuards();

        // Enviar heartbeat inicial
        this.sendHeartbeat();

        // Configurar heartbeat periódico cada 30 segundos
        // (se limpia el anterior por si registerAsCentral se llama más de una vez)
        if (this.heartbeatInterval) {
          clearInterval(this.heartbeatInterval);
        }
        this.heartbeatInterval = setInterval(() => {
          this.sendHeartbeat();
        }, 30000);

      } else {
        console.warn('⚠️ Socket desconectado durante el proceso de registro');
      }
    }, 3000);
  }

  // Métodos públicos mejorados

  // Según PANIC_BUTTON_FRONTEND_GUIDE: acknowledge_alert con alert_id
  acknowledgePanicAlert(alertId: string): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se puede reconocer alerta');
      return;
    }

    const ackData = {
      alert_id: alertId  // Solo el UUID de la alerta según la guía
    };

    console.log('✅ Reconociendo alerta:', ackData);
    this.socket.emit('acknowledge_alert', ackData);
  }

  resolvePanicAlert(alertId: string, resolution?: string): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se puede resolver alerta');
      return;
    }

    const userData = this.getUserData();
    
    const resolveData = {
      alert_id: alertId,
      operator_id: userData.id,
      operator_name: userData.name,
      resolution_notes: resolution || '',
      timestamp: new Date().toISOString()
    };

    console.log('🔒 Resolviendo alerta:', resolveData);
    this.socket.emit('resolve_alert', resolveData);
  }

  // Método para obtener estado del sistema
  getSystemStatus(): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.socket.connected) {
        reject(new Error('Socket no conectado'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Timeout obteniendo estado del sistema'));
      }, 10000);

      this.socket.emit('get_system_status', (response: any) => {
        clearTimeout(timeout);
        resolve(response);
      });
    });
  }
  
  private getUserData(): { id: string, name: string } {
    try {
      const tokenData = this.helps.getToken();
      const userData = tokenData?.data as Users;
      return {
        id: tokenData?.id ,
        name: userData?.name || userData?.name || 'Admin'
      };
    } catch (error) {
      console.warn('⚠️ Error obteniendo datos de usuario:', error);
      return { id: 'admin_fallback', name: 'Admin' };
    }
  }

  // Enviar heartbeat para operadores de central
  private sendHeartbeat(): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('central_heartbeat', {
        operator_id: this.getUserData().id
      });
    }
  }

  // Send keepalive ping - MEJORADO
  sendPing(): void {
    if (this.socket && this.socket.connected) {
      console.log('🏓 Enviando ping...');
      const pingData = {
        client_type: 'angular_admin',
        timestamp: new Date().toISOString(),
        socket_id: this.socket.id
      };
      
      this.socket.emit('ping', pingData);
      
      // Escuchar respuesta pong
      this.socket.once('pong', (data: any) => {
        console.log('🏓 Pong recibido:', data);
        console.log('✅ Conexión WebSocket verificada correctamente');
      });
      
      // Timeout para ping
      setTimeout(() => {
        console.log('🏓 Ping test completado');
      }, 5000);
    } else {
      console.warn('⚠️ No se puede enviar ping - socket no conectado');
    }
  }

  // Check if WebSocket is connected
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Get connection info
  getConnectionInfo(): any {
    return {
      connected: this.socket?.connected || false,
      id: this.socket?.id,
      transport: this.socket?.io?.engine?.transport?.name,
      url: environment.websocketUrl,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts
    };
  }

  // Test connection with ping
  testConnection(): void {
    if (this.socket && this.socket.connected) {
      console.log('Testing WebSocket connection with ping...');
      this.socket.emit('ping', { test: true });

      // Listen for pong response
      this.socket.once('pong', (data: any) => {
        console.log('WebSocket connection test successful - received pong:', data);
      });

      // Timeout for ping test
      setTimeout(() => {
        console.log('WebSocket ping test completed');
      }, 3000);
    } else {
      console.warn('Cannot test connection - WebSocket not connected');
    }
  }

  // Force reconnection attempt
  forceReconnect(): void {
    console.log('Forcing WebSocket reconnection...');
    this.disconnect();
    setTimeout(() => {
      this.reconnectAttempts = 0;
      this.connect();
    }, 1000);
  }

  // Request current active guards
  requestActiveGuards(): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se pueden solicitar guardias');
      return;
    }

    console.log('📋 Solicitando guardias activos...');
    this.socket.emit('request_active_guards');
  }

  // Send message to specific guard
  sendMessageToGuard(guardId: string, message: string): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se puede enviar mensaje');
      return;
    }

    const messageData = {
      guard_id: guardId,
      message: message
    };

    console.log('💬 Enviando mensaje a guardia:', messageData);
    this.socket.emit('send_message_to_guard', messageData);

    // Add message to local chat immediately (optimistic UI)
    const chatMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'outgoing',
      content: message,
      timestamp: new Date(),
      sender: 'Central',
      guard_id: guardId
    };

    this.chatMessagesSubject.next(chatMessage);
  }

  // Send message to all guards in facility
  sendMessageToFacility(facilityId: string, message: string): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se puede enviar mensaje');
      return;
    }

    const messageData = {
      facility_id: facilityId,
      message: message
    };

    console.log('💬 Enviando mensaje a instalación:', messageData);
    this.socket.emit('send_message_to_guard', messageData);
  }

  // Send message to all guards
  sendBroadcastMessage(message: string): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se puede enviar mensaje');
      return;
    }

    const messageData = {
      target: 'all',
      message: message
    };

    console.log('📢 Enviando mensaje broadcast:', messageData);
    this.socket.emit('send_message_to_guard', messageData);
  }

  // Request chat history for specific guard
  requestChatHistory(guardId: string): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se puede solicitar historial');
      return;
    }

    // Store the current request guard ID for later use in the response
    this.currentHistoryRequestGuardId = guardId;
    this.currentHistoryRequestFacilityId = null; // Clear facility request

    const requestData = {
      guard_id: guardId
    };

    console.log('📜 Solicitando historial de chat:', requestData);
    this.socket.emit('request_history', requestData);
  }

  // Request chat history for facility
  requestFacilityChatHistory(facilityId: string): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se puede solicitar historial');
      return;
    }

    // Store the current facility request for later use in the response
    this.currentHistoryRequestFacilityId = facilityId;
    this.currentHistoryRequestGuardId = null; // Clear guard request

    const requestData = {
      facility_id: facilityId
    };

    console.log('📜 Solicitando historial de chat de instalación:', requestData);
    this.socket.emit('request_history', requestData);
  }

  // Request all chat history
  requestAllChatHistory(): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('⚠️ Socket no conectado, no se puede solicitar historial');
      return;
    }

    const requestData = {
      target: 'all'
    };

    console.log('📜 Solicitando historial de chat completo:', requestData);
    this.socket.emit('request_history', requestData);
  }

  // Set facilities to monitor and re-register if connected
  setFacilitiesToMonitor(facilities: any[]): void {
    // Extract facility IDs, assuming facilities have an 'id' field
    this.facilitiesToMonitor = facilities.map(f => f.id || f.facility_id || f.toString());
    console.log('🏢 Instalaciones configuradas para monitoreo:', this.facilitiesToMonitor);

    // If already connected, re-register with new facilities
    if (this.socket && this.socket.connected) {
      console.log('🔄 Re-registrando con nuevas instalaciones...');
      this.registerAsCentral();
    }
  }

  // Get current facilities being monitored
  getFacilitiesToMonitor(): string[] {
    return [...this.facilitiesToMonitor];
  }

  // Test method to emit panic alert
  testPanicAlert(): void {
    const testAlert: PanicAlertData = {
      id: 'test_websocket_' + Date.now(),
      guard_id: '999',
      guard_name: 'Test Guard WebSocket',
      facility_id: '1',
      facility_name: 'Test Facility WebSocket',
      alert_type: 'panic_button',
      description: 'Test panic alert from WebSocket service',
      priority: 'critical',
      created_at: new Date().toISOString()
    };

    console.log('🧪 WEBSOCKET SERVICE: Enviando alerta de pánico de prueba:', testAlert);
    this.panicAlertSubject.next(testAlert);
  }

  // Helper method to determine message type from history and new messages
  private determineMessageType(msg: any): 'incoming' | 'outgoing' | 'system' {
    // Verificar tipo de sistema primero
    if (msg.type === 'system') {
      return 'system';
    }

    // Según FRONTEND_ANGULAR_GUIDE: mensajes de guardias tienen type: 'from_guard'
    if (msg.type === 'from_guard' || msg.from_guard_id) {
      return 'incoming';
    }

    // Mensajes de operadores tienen type: 'from_operator'
    if (msg.type === 'from_operator' || msg.from_operator_id) {
      return 'outgoing';
    }

    // Fallback: si tiene guard_id pero no from_operator_id, es de guardia
    if (msg.guard_id && !msg.from_operator_id) {
      return 'incoming';
    }

    // Por defecto: mensaje entrante
    return 'incoming';
  }

  // Método para suscribirse a eventos específicos
  on(eventName: string): Observable<any> {
    return new Observable(observer => {
      if (!this.socket) {
        observer.error('Socket no disponible');
        return;
      }

      const handler = (data: any) => {
        console.log(`📡 Evento ${eventName} recibido:`, data);
        observer.next(data);
      };

      this.socket.on(eventName, handler);

      // Cleanup function
      return () => {
        if (this.socket) {
          this.socket.off(eventName, handler);
        }
      };
    });
  }

}